// src/pages/demo/DemoDashboard.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { lessonPlansAPI, contentLibraryAPI, studentsAPI, homeworkScheduleAPI } from '../../services/api';
import ClassManagementModal from '../../components/ClassManagementModal';
import UserGuideOverlay from '../../components/UserGuideOverlay';
import { getGuideLoginCount, hasShownThisSession, markShownThisSession, setGuideUser } from '../../utils/guideSession';
import { canAccessJcq } from '../../utils/jcqAccess';
import { auth } from '../../firebase';

// --- BRAND PALETTE ---
const BRAND = {
    teal: '#75B9BE',
    cream: '#FDF6EC',
    charcoal: '#2D4042',
    coral: '#F28482',
    white: '#FFFFFF',
    grey: '#F0F2F5',
    danger: '#E07A5F',
    warning: '#F2CC8F'
};

// +++ DEPENDENT DROPDOWN MAPPING +++
const SUBJECT_OPTIONS = {
    'Year 1':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 2':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 3':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 4':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 5':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 6':  ['English', 'Science', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 7':  ['English', 'Biology', 'Chemistry', 'Physics', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 8':  ['English', 'Biology', 'Chemistry', 'Physics', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 9':  ['English', 'Biology', 'Chemistry', 'Physics', 'Computing', 'Geography', 'History', 'Religious Education'],
    'Year 10': ['English', 'Biology', 'Chemistry', 'Physics', 'History', 'Geography', 'Computer Science', 'Business Studies', 'Media Studies', 'Psychology', 'Sociology', 'Religious Studies'],
    'Year 11': ['English', 'Biology', 'Chemistry', 'Physics', 'History', 'Geography', 'Computer Science', 'Business Studies', 'Media Studies', 'Psychology', 'Sociology', 'Religious Studies'],
};

const DemoDashboard = ({ user, onLogout, isDemoMode, routeNamespace = 'standard' }) => {
    const [lessonPlans, setLessonPlans] = useState([]);
    const [title, setTitle] = useState('');
    
    // State for Context Generation
    const [subject, setSubject] = useState('');
    const [yearGroup, setYearGroup] = useState('');

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    
    // Show Tutorials State
    const [showTutorials, setShowTutorials] = useState(false);
    const [isClassManagementOpen, setIsClassManagementOpen] = useState(false);
    const [guideConfig, setGuideConfig] = useState({ isOpen: false, mode: 'manual', autoStep: null });

    const [libraryCount, setLibraryCount] = useState(null);
    const [studentCount, setStudentCount] = useState(null);
    const showJcqButton = canAccessJcq(user);

    const fetchLessonPlans = async () => {
        try {
            setLoading(true);
            const res = await lessonPlansAPI.getAll();
            setLessonPlans(res.data.lessonPlans);
        } catch (err) {
            setError('Failed to fetch lesson plans.');
        } finally {
            setLoading(false);
        }
    };
    
    useEffect(() => {
        fetchLessonPlans();
        contentLibraryAPI.getMeta().then(r => setLibraryCount(r.data?.totalLessons ?? null)).catch(() => {});
        studentsAPI.count().then(r => setStudentCount(r.data.count)).catch(() => {});
    }, []);

    const guideUserId = user?.uid || user?.hashedPassword || auth.currentUser?.uid || null;

    useEffect(() => {
        setGuideUser(guideUserId);
    }, [guideUserId]);


    const hasLessonPlans = lessonPlans.length > 0;
    const hasClasses     = lessonPlans.some(plan => plan.schedules?.length > 0);
    const hasTutorials   = lessonPlans.some(plan =>
        plan.schedules?.some(s => s.homeworks?.length > 0)
    );

    useEffect(() => {
        if (loading || !guideUserId) return;

        const loginCount = getGuideLoginCount(guideUserId);
        if (loginCount < 1 || loginCount > 3) return;

        let autoStep = 1;
        if (hasLessonPlans && !hasClasses) {
            autoStep = 2;
        } else if (hasClasses) {
            autoStep = '34';
        }

        const guideKey = `dashboard_login_${loginCount}_${autoStep}`;
        if (!hasShownThisSession(guideKey)) {
            markShownThisSession(guideKey);
            setGuideConfig({ isOpen: true, mode: 'auto', autoStep });
        }
    }, [loading, guideUserId, hasLessonPlans, hasClasses]);

    const handleGuideClose = () => {
        setGuideConfig({ isOpen: false, mode: 'manual', autoStep: null });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const uid = guideUserId;
            const normalizedRegistrationCode = String(user?.registrationCodeUsed || '').trim().toUpperCase();
            const currentPlatformMode = normalizedRegistrationCode === 'TEST R_26'
                ? 'standard'
                : (localStorage.getItem(uid ? `documentUploadMode_${uid}` : 'documentUploadMode') || 'standard');
            const currentStudentProfileMode = normalizedRegistrationCode === 'CL26' ? 'supply_teacher' : 'default';
            
            await lessonPlansAPI.create({ 
                title, 
                numberOfLessons: 1, // Satisfies DB requirement
                subject, 
                yearGroup,
                platformMode: currentPlatformMode, 
                studentProfileMode: currentStudentProfileMode 
            });
            
            setTitle('');
            setSubject('');
            setYearGroup('');
            setError('');
            
            fetchLessonPlans();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create lesson plan.');
        }
    };

    const handleDelete = async (planId, planTitle) => {
        if (window.confirm(`Are you sure you want to delete "${planTitle}"?`)) {
            try {
                await lessonPlansAPI.delete(planId);
                fetchLessonPlans();
            } catch (err) {
                setError('Failed to delete lesson plan.');
            }
        }
    };

    const handleDeleteClass = async (schedule) => {
        const n = (schedule.students || []).length;
        const msg = `Delete class "${schedule.name}"?\n\nThis permanently removes the class` +
            (n ? ` and its ${n} student${n === 1 ? '' : 's'}` : '') +
            `, along with their tutorials and homeworks. This cannot be undone.`;
        if (!window.confirm(msg)) return;
        try {
            await homeworkScheduleAPI.deleteById(schedule.id);
            fetchLessonPlans();
        } catch (err) {
            setError('Failed to delete class.');
        }
    };

    const handleAddClass = async (planId) => {
        const name = window.prompt('Name the new class (e.g. "Year 4 Science"):');
        if (!name || !name.trim()) return;
        try {
            await homeworkScheduleAPI.create({ name: name.trim(), lessonPlanId: planId });
            fetchLessonPlans();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to add class.');
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', fontFamily: 'Nunito, sans-serif', color: BRAND.charcoal, background: BRAND.cream, minHeight: '100vh', fontSize: '1.2rem', fontWeight: '700' }}>
                Loading your classroom... 🚀
            </div>
        );
    }

    const displayName = user?.firstName || user?.displayName?.split(' ')[0] || 'Teacher';

    const ns = routeNamespace === 'lite' ? '/lite' : '';
    const lessonRoute = (id) => `${ns}/lessons/${id}`;
    const profilesRoute = (scheduleId) => `${ns}/profiles/${scheduleId}`;
    const contentLibraryRoute = `${ns}/content-library`;
    const homeworkRoute = (homeworkId) => `${ns}/homework/${homeworkId}`;

    return (
        <div style={{ 
            fontFamily: "'Inter', sans-serif",
            backgroundColor: BRAND.cream,
            minHeight: '100vh',
            color: BRAND.charcoal,
            position: 'relative', 
            overflow: 'hidden'    
        }}>
            
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=Nunito:wght@700;800&display=swap');
                    
                    h1, h2, h3, h4 { font-family: 'Nunito', sans-serif; }

                    .pattern-bg {
                        background-image: radial-gradient(${BRAND.teal} 15%, transparent 16%), radial-gradient(${BRAND.coral} 15%, transparent 16%);
                        background-size: 60px 60px;
                        background-position: 0 0, 30px 30px;
                        opacity: 0.1;
                        position: absolute;
                        top: 0; left: 0; right: 0; bottom: 0;
                        z-index: 0;
                        pointer-events: none;
                        mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                        -webkit-mask-image: linear-gradient(to bottom, black 0%, transparent 100%);
                    }

                    .dash-card {
                        background: ${BRAND.white};
                        border-radius: 16px;
                        border: 2px solid ${BRAND.charcoal};
                        box-shadow: 4px 4px 0px rgba(45, 64, 66, 0.1);
                        transition: transform 0.2s ease, box-shadow 0.2s ease;
                        overflow: hidden;
                    }
                    .dash-card:hover {
                        transform: translateY(-2px);
                        box-shadow: 6px 6px 0px rgba(45, 64, 66, 0.15);
                    }
                    
                    .dash-input {
                        padding: 12px 16px;
                        border-radius: 10px;
                        border: 2px solid #E0E0E0;
                        background-color: #F8F9FA;
                        width: 100%;
                        box-sizing: border-box;
                        transition: all 0.2s;
                        outline: none;
                        font-family: 'Inter', sans-serif;
                        font-weight: 500;
                        font-size: 0.95rem;
                        color: ${BRAND.charcoal};
                    }
                    .dash-input:focus {
                        background-color: white;
                        border-color: ${BRAND.teal};
                    }

                    .btn-base {
                        border: none;
                        border-radius: 10px;
                        padding: 10px 20px;
                        font-family: 'Nunito', sans-serif;
                        font-weight: 800;
                        cursor: pointer;
                        transition: all 0.1s ease;
                        font-size: 0.95rem;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        border-bottom: 4px solid rgba(0,0,0,0.1); 
                    }
                    .btn-base:active {
                        transform: translateY(2px);
                        border-bottom: 2px solid rgba(0,0,0,0.1);
                        margin-bottom: 2px;
                    }

                    .btn-teal { background-color: ${BRAND.teal}; color: white; }
                    .btn-coral { background-color: ${BRAND.coral}; color: white; }
                    .btn-jcq { background-color: #FF7F50; color: white; }
                    
                    .btn-logout {
                        background-color: white;
                        color: ${BRAND.charcoal};
                        border: 2px solid ${BRAND.charcoal};
                        border-bottom: 4px solid ${BRAND.charcoal};
                        padding: 6px 16px;
                        font-size: 0.85rem;
                    }
                    .btn-logout:active {
                        border-bottom: 2px solid ${BRAND.charcoal};
                    }
                    .btn-logout:hover {
                        background-color: #F9F9F9;
                    }

                    .btn-icon-only {
                        width: 36px;
                        height: 36px;
                        padding: 0;
                        justify-content: center;
                        border-radius: 8px;
                        background: #F0F0F0;
                        color: ${BRAND.charcoal};
                        border: 2px solid transparent;
                        border-bottom: 3px solid #DDD;
                        font-size: 1.1rem;
                    }
                    .btn-icon-only:hover { background: white; border-color: ${BRAND.charcoal}; }
                    .btn-icon-only:active { border-bottom: 0px solid transparent; transform: translateY(3px); }

                    .toggle-switch { position: relative; display: inline-block; width: 44px; height: 24px; }
                    .toggle-switch input { opacity: 0; width: 0; height: 0; }
                    .slider {
                        position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0;
                        background-color: #E0E0E0; transition: .3s; border-radius: 34px;
                    }
                    .slider:before {
                        position: absolute; content: ""; height: 18px; width: 18px; left: 3px; bottom: 3px;
                        background-color: white; transition: .3s; border-radius: 50%;
                    }
                    input:checked + .slider { background-color: ${BRAND.teal}; }
                    input:checked + .slider:before { transform: translateX(20px); }

                    @media (max-width: 899px) {
                        .user-guide-label { display: none !important; }
                    }
                `}
            </style>

            <div className="pattern-bg"></div>


            <div style={{ position: 'relative', zIndex: 1, padding: '40px 20px', maxWidth: '1100px', margin: '0 auto' }}>
                
                {/* HEADER */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '40px', gap: '20px' }}>
                    <div>
                        <h1 style={{ margin: '0 0 5px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '2.4em', letterSpacing: '-0.5px' }}>
                            Welcome, {displayName} 👋
                        </h1>
                        <p style={{ margin: 0, color: '#5D7375', fontSize: '1.1rem', fontWeight: '500' }}>
                            Ready to plan some great lessons?
                            {studentCount != null && (
                                <span style={{ marginLeft: '12px', fontWeight: '800', color: BRAND.charcoal, background: BRAND.cream, padding: '3px 10px', borderRadius: '8px', border: '1px solid #E0E0E0', fontSize: '0.95rem' }}>
                                    👥 {studentCount} student{studentCount === 1 ? '' : 's'}
                                </span>
                            )}
                        </p>
                    </div>
                    
                    {/* BUTTON GROUP */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', alignItems: 'flex-end' }}>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                {showJcqButton && (
                                    <Link className="btn-base btn-jcq" to="/jcq-hub" style={{ textDecoration: 'none' }}>
                                        JCQ
                                    </Link>
                                )}
                                <button className="btn-base btn-teal" onClick={() => setIsClassManagementOpen(true)}>
                                    My Account
                                </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <label className="user-guide-label" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#2D4042', fontWeight: '800', fontFamily: 'Nunito, sans-serif', margin: 0 }}>
                                    <label className="toggle-switch">
                                        <input type="checkbox" checked={guideConfig.isOpen} onChange={(e) => setGuideConfig(e.target.checked ? { isOpen: true, mode: 'manual', autoStep: null } : { isOpen: false, mode: 'manual', autoStep: null })} />
                                        <span className="slider"></span>
                                    </label>
                                    User Guide
                                </label>
                                <button onClick={() => { setGuideUser(null); onLogout(); }} className="btn-base btn-logout">
                                    Log Out
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* CREATE PLAN CARD */}
                <div data-guide="create-card" className="dash-card" style={{ padding: '30px', marginBottom: '35px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '30px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 300px' }}>
                        <h3 style={{ margin: '0 0 5px 0', color: BRAND.charcoal, fontWeight: '800', fontSize: '1.4rem' }}>Create New Lesson Plan</h3>
                        <p style={{ margin: 0, color: '#5D7375', fontSize: '0.95em' }}>Give it a name and set the subject context.</p>
                    </div>

                    {/* +++ UPDATED GRID: Dependent Dropdown + Specific Column Widths +++ */}
                    <form data-guide="create-form" onSubmit={handleSubmit} style={{
                        display: 'grid',
                        gridTemplateColumns: 'minmax(200px, 3fr) 130px 155px 120px',
                        gap: '15px',
                        flex: '2 1 400px',
                        alignItems: 'end'
                    }}>
                        {/* Column 1: Plan Name */}
                        <div style={{ gridColumn: '1' }}>
                            <label style={{display:'block', marginBottom:'6px', fontSize:'0.8em', fontWeight:'700', color: BRAND.charcoal, textTransform:'uppercase', letterSpacing:'0.5px'}}>Plan Name</label>
                            <input
                                data-guide="create-input"
                                className="dash-input"
                                type="text"
                                placeholder="e.g. Geography - Rivers"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                required
                            />
                        </div>

                        {/* Column 2: Year Group */}
                        <div style={{ gridColumn: '2' }}>
                            <label style={{display:'block', marginBottom:'6px', fontSize:'0.8em', fontWeight:'700', color: BRAND.charcoal, textTransform:'uppercase', letterSpacing:'0.5px'}}>Year</label>
                            <select 
                                className="dash-input" 
                                value={yearGroup} 
                                onChange={(e) => {
                                    setYearGroup(e.target.value);
                                    setSubject(''); // Reset subject when year changes
                                }} 
                                required
                            >
                                <option value="" disabled>Select...</option>
                                {Object.keys(SUBJECT_OPTIONS).map(year => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </div>

                        {/* Column 3: Subject (Dependent) */}
                        <div style={{ gridColumn: '3' }}>
                            <label style={{display:'block', marginBottom:'6px', fontSize:'0.8em', fontWeight:'700', color: BRAND.charcoal, textTransform:'uppercase', letterSpacing:'0.5px'}}>Subject</label>
                            <select 
                                className="dash-input" 
                                value={subject} 
                                onChange={(e) => setSubject(e.target.value)} 
                                required 
                                disabled={!yearGroup}
                            >
                                <option value="" disabled>{yearGroup ? 'Select...' : 'Year First'}</option>
                                {yearGroup && SUBJECT_OPTIONS[yearGroup]?.map(sub => (
                                    <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                        </div>

                        {/* Column 4: Submit Button */}
                        <button type="submit" className="btn-base btn-coral" style={{ gridColumn: '4', whiteSpace: 'nowrap', height: '46px', width: '100%', justifyContent: 'center' }}>
                            + Create
                        </button>
                    </form>
                    {error && <p style={{ color: BRAND.danger, margin: 0, fontSize: '0.9em', width: '100%', fontWeight: 'bold', flexBasis: '100%' }}>⚠️ {error}</p>}
                </div>

                {/* CONTENT LIBRARY BANNER */}
                <Link to={contentLibraryRoute} state={{ fresh: true }} style={{ textDecoration: 'none', display: 'block', marginBottom: '20px' }}>
                    <div className="dash-card" style={{ border: `2px solid ${BRAND.teal}`, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', cursor: 'pointer' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            <span style={{ fontSize: '1.8rem' }}>📚</span>
                            <div>
                                <div style={{ fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '1.05rem', color: BRAND.charcoal }}>Content Library</div>
                                <div style={{ fontSize: '0.83em', color: '#5D7375', marginTop: '2px' }}>
                                    {libraryCount !== null ? `${libraryCount} pre-built Oak Academy lessons` : 'Pre-built Oak Academy lessons'} — ready to use instantly
                                </div>
                            </div>
                        </div>
                        <span style={{ background: BRAND.teal, color: 'white', fontFamily: 'Nunito, sans-serif', fontWeight: '800', fontSize: '0.85em', padding: '8px 16px', borderRadius: '10px', whiteSpace: 'nowrap' }}>Browse →</span>
                    </div>
                </Link>

                {/* LESSON PLANS HEADER + TOGGLES */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px', borderBottom: `2px solid ${BRAND.charcoal}`, paddingBottom: '10px' }}>
                    <h2 style={{ margin: 0, color: BRAND.charcoal, fontWeight: '800', fontSize: '1.8em' }}>Your Lesson Plans</h2>
                    
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <label data-guide="show-tutorials" style={{
                            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
                            fontSize: '0.85rem', color: BRAND.charcoal, fontWeight: '800', fontFamily: 'Nunito, sans-serif'
                        }}>
                            <label className="toggle-switch">
                                <input
                                    type="checkbox"
                                    checked={showTutorials}
                                    onChange={(e) => setShowTutorials(e.target.checked)}
                                />
                                <span className="slider"></span>
                            </label>
                            <span>Show Tutorials</span>
                        </label>
                    </div>
                </div>

                {lessonPlans.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#888', border: '3px dashed #CCD', borderRadius: '16px', background: 'rgba(255,255,255,0.6)' }}>
                        <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: BRAND.charcoal }}>It's quiet here...</h3>
                        <p>Create your first plan above to get started!</p>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '30px', alignItems: 'start' }}>
                        {lessonPlans.map((plan, index) => {
                            const isOA = !!plan.isOakAcademy;
                            // oakScheduleId is the unnamed placeholder schedule for adding a class;
                            // fall back to first named schedule, then lesson plan page
                            const oakScheduleId = plan.oakScheduleId || plan.schedules?.[0]?.id;
                            const planLink = isOA && oakScheduleId
                                ? profilesRoute(oakScheduleId)
                                : lessonRoute(plan.id);
                            return (
                            <div key={plan.id} className="dash-card" style={{ display: 'flex', flexDirection: 'column' }}>

                                <div style={{
                                    padding: '20px', background: '#F8F9FA', borderBottom: '2px solid #EAEAEA',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                                    position: 'relative', minHeight: '48px'
                                }}>
                                    <div style={{ paddingRight: '36px' }}>
                                        <Link to={planLink} style={{ textDecoration: 'none', color: BRAND.charcoal }}>
                                            <h3 data-guide={index === 0 ? "plan-name" : undefined} style={{ margin: '0 0 6px 0', fontSize: '1.25em', fontWeight: '800' }}>{plan.title}</h3>
                                        </Link>
                                    </div>
                                    <div style={{ position: 'absolute', bottom: '10px', right: '10px', display: 'flex', gap: '4px' }}>
                                        {isOA && (
                                            <span style={{ fontSize: '0.7em', fontWeight: '800', color: '#065F46', background: '#D1FAE5', padding: '2px 7px', borderRadius: '999px', border: '1px solid #6EE7B7', letterSpacing: '0.5px' }}>
                                                Oak Academy
                                            </span>
                                        )}
                                        {!isOA && plan.platformMode === 'knowledge_organiser' && (
                                            <span style={{ fontSize: '0.7em', fontWeight: '800', color: '#006064', background: '#E0F7FA', padding: '2px 7px', borderRadius: '999px', border: '1px solid #80DEEA', letterSpacing: '0.5px' }}>
                                                KO
                                            </span>
                                        )}
                                        {!isOA && plan.studentProfileMode === 'supply_teacher' && (user?.registrationCodeUsed || '').trim() !== 'CL26' && (
                                            <span style={{ fontSize: '0.7em', fontWeight: '800', color: '#7B3F00', background: '#FFF3E0', padding: '2px 7px', borderRadius: '999px', border: '1px solid #FFCC80', letterSpacing: '0.5px' }}>
                                                SP
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ position: 'absolute', top: '10px', right: '10px' }}>
                                        <button
                                            className="btn-base btn-icon-only"
                                            onClick={() => handleDelete(plan.id, plan.title)}
                                            title="Delete Plan"
                                            style={{ color: BRAND.danger }}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>

                                <div style={{ padding: '20px', flex: 1 }}>
                                    {isOA ? (() => {
                                        const namedSchedules = (plan.schedules || []).filter(s => s.name && s.name !== 'Unnamed Class');
                                        return namedSchedules.length > 0 ? (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                {namedSchedules.map(s => (
                                                    <Link key={s.id} to={profilesRoute(s.id)} style={{ textDecoration: 'none', fontWeight: '800', color: 'white', display: 'flex', alignItems: 'center', gap: '6px', background: BRAND.teal, borderRadius: '10px', padding: '8px 10px', border: `2px solid ${BRAND.charcoal}` }}>
                                                        📅 Class: {s.name}
                                                    </Link>
                                                ))}
                                                <Link to={planLink} style={{ textDecoration: 'none' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 10px', background: '#F0F2F5', borderRadius: '10px', color: BRAND.charcoal, fontWeight: '700', fontSize: '0.88em' }}>
                                                        + Add Another Class
                                                    </div>
                                                </Link>
                                            </div>
                                        ) : (
                                            <Link to={planLink} style={{ textDecoration: 'none' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: BRAND.teal, borderRadius: '10px', border: `2px solid ${BRAND.charcoal}`, color: 'white', fontWeight: '800', fontSize: '0.95em' }}>
                                                    📚 View Lessons & Add Class
                                                </div>
                                            </Link>
                                        );
                                    })() : (
                                      <>
                                        {plan.schedules && plan.schedules.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                            {plan.schedules.map((schedule, scheduleIndex) => (
                                                <div key={schedule.id} style={{ fontSize: '0.95em' }}>
                                                    <div style={{ display: 'flex', alignItems: 'stretch', gap: '6px', marginBottom: '6px' }}>
                                                        <Link
                                                            data-guide={index === 0 && scheduleIndex === 0 ? "first-class" : undefined}
                                                            to={profilesRoute(schedule.id)}
                                                            style={{
                                                                flex: 1, minWidth: 0, fontWeight: '800', color: 'white', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px',
                                                                background: BRAND.teal, borderRadius: '10px', padding: '8px 10px', border: `2px solid ${BRAND.charcoal}`
                                                            }}
                                                        >
                                                            📅 Class: {schedule.name}
                                                        </Link>
                                                        <button
                                                            onClick={() => handleDeleteClass(schedule)}
                                                            title="Delete class"
                                                            style={{ flexShrink: 0, background: '#FFFFFF', color: BRAND.danger, border: `2px solid ${BRAND.charcoal}`, borderRadius: '10px', padding: '0 12px', fontWeight: '800', fontSize: '0.9em', cursor: 'pointer' }}
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>

                                                    {showTutorials ? (((schedule.homeworks || []).filter(hw => hw?.isJcqTest !== true).length > 0) ? (
                                                        <ul style={{ paddingLeft: '0', listStyle: 'none', margin: 0 }}>
                                                            {(schedule.homeworks || []).filter(hw => hw?.isJcqTest !== true).map(hw => (
                                                                <li key={hw._id} style={{
                                                                    padding: '8px 10px', marginTop: '6px',
                                                                    backgroundColor: '#F3F4F6', borderRadius: '8px',
                                                                    border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column'
                                                                }}>
                                                                    <Link to={homeworkRoute(hw._id)} style={{ textDecoration: 'none', color: BRAND.charcoal, fontWeight: '600' }}>
                                                                        📝 Lesson {hw.lessonNumber} - Tutorial {hw.homeworkNumber}
                                                                    </Link>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9em', marginLeft: '24px' }}>No tutorials yet.</span>
                                                    )) : null}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div style={{ padding: '15px 0', textAlign: 'center', color: '#999', fontSize: '0.9em', fontStyle: 'italic' }}>
                                            No classes yet.
                                        </div>
                                    )}
                                        <button
                                            onClick={() => handleAddClass(plan.id)}
                                            style={{ marginTop: '10px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px 10px', background: '#F0F2F5', borderRadius: '10px', color: BRAND.charcoal, fontWeight: '700', fontSize: '0.9em', border: `2px dashed ${BRAND.charcoal}`, cursor: 'pointer' }}
                                        >
                                            + Add Class
                                        </button>
                                      </>
                                    )}
                                </div>

                                {!isOA && (
                                <div style={{ padding: '12px 20px', background: BRAND.cream, borderTop: `2px solid ${BRAND.charcoal}`, textAlign: 'right' }}>
                                    <Link to={lessonRoute(plan.id)} style={{ textDecoration: 'none', fontSize: '0.9em', fontWeight: '800', color: BRAND.charcoal, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                        Manage Plan <span style={{ color: BRAND.coral }}>➜</span>
                                    </Link>
                                </div>
                                )}
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ClassManagementModal
                isOpen={isClassManagementOpen}
                onClose={() => setIsClassManagementOpen(false)}
                uid={user?.uid}
            />

            {guideConfig.isOpen && (
                <UserGuideOverlay
                    onClose={handleGuideClose}
                    mode={guideConfig.mode}
                    autoStep={guideConfig.autoStep}
                    hasLessonPlans={hasLessonPlans}
                    hasClasses={hasClasses}
                    hasTutorials={hasTutorials}
                />
            )}
        </div>
    );
};

export default DemoDashboard;
